<template>
  <div v-if="hasOptionsToShow || searchFunction" class="pe-select-wrapper" :class="{ 'is-disabled': isDisabledComp }">

    <div class="pe-input-group">

      <div v-if="computedPlaceholder !== defaultPlaceholder && $slots.selected && !inputFocused"
        class="pe-input pe-input--fake" @click="handleFocusAndSearch" tabindex="0" @blur="hideDropdownWithDelay">
        <slot name="selected" :selected="selected" />
      </div>

      <input ref="searchInput" v-model="searchQuery" type="text" :placeholder="computedPlaceholder"
        class="pe-input pe-search-input" :class="[
          selected ? globalConfig?.classes?.input : globalConfig?.classes?.placeholder,
          {
            'pe-input--has-selection': hasSelection,
            'pe-input--hidden': computedPlaceholder !== defaultPlaceholder && $slots.selected && !inputFocused
          }]" :disabled="isDisabledComp" @focus="handleFocus" @blur="hideDropdownWithDelay" @keydown="handleKeydown" />

      <ul v-if="showDropdown && shouldShowDropdown && !isDisabledComp" ref="dropdownListRef" class="pe-dropdown-list"
        :class="{
          'pe-dropdown-list--overlay': overlayDropdown,
          'pe-dropdown-list--upwards': overlayDropdown && openUpwards,
        }">
        <li v-if="isLoading" class="pe-dropdown-item pe-dropdown-item--info"
          :class="globalConfig?.classes?.placeholderSelect">
          Cargando...
        </li>

        <template v-else>
          <li v-for="(option, index) in filteredOptions" :key="index" class="pe-dropdown-item" :class="[
            isSelected(option) ? globalConfig?.classes?.input : globalConfig?.classes?.placeholderSelect, {
              'pe-dropdown-item--selected': isSelected(option),
              'pe-dropdown-item--disabled': isDisabled(option),
              'pe-dropdown-item--highlighted': index === highlightedIndex
            }
          ]" @mousedown.prevent="selectOption(option)" @mouseover="highlightedIndex = index">
            <slot :option="option" :index="index">
              {{ displayValue(option) }}
            </slot>
          </li>
        </template>

        <li v-if="!isLoading && filteredOptions.length === 0" class="pe-dropdown-item pe-dropdown-item--info"
          :class="globalConfig?.classes?.placeholderSelect">
          No hay coincidencias
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, nextTick, inject } from "vue";

export default {
  name: "SearchableSelect",
  props: {
    options: { type: Array, required: true, default: () => [] },
    searchFunction: { type: Function, default: null },
    debounceDelay: { type: Number, default: 400 },
    disabledItems: { type: Array, default: () => [] },
    selected: { type: [Object, String, Number, Array], default: null },
    useLastAsPlaceholder: { type: Boolean, default: false },
    placeholderKey: { type: [String, Array], default: "title" },
    displayKeys: { type: [String, Array], default: null },
    defaultPlaceholder: { type: String, default: "Selecciona una opción" },
    filterKeys: { type: Array, default: () => [] },
    isDisabledComponent: { type: Boolean, default: false },
    overlayDropdown: { type: Boolean, default: false },
    openUpwards: { type: Boolean, default: false },
  },

  emits: ["select", "dropdown-opened"],

  setup(props, { emit }) {
    const showDropdown = ref(false);
    const searchInput = ref(null);
    const dropdownListRef = ref(null);
    const searchQuery = ref("");
    const internalSelected = ref(props.selected);
    const highlightedIndex = ref(-1); // --- Índice para teclado
    const warnedKeys = new Set();
    const inputFocused = ref(false);
    const suppressNextHide = ref(false);
    const isLoading = ref(false);
    const asyncOptions = ref([]);
    const globalConfig = inject('engineConfig');
    let debounceTimer = null;

    watch(() => props.options, (newVal) => {
      if (props.searchFunction) asyncOptions.value = newVal;
    }, { immediate: true });

    watch(() => props.selected, (newVal) => {
      if (JSON.stringify(newVal) !== JSON.stringify(internalSelected.value)) {
        internalSelected.value = newVal;
        if (newVal && searchQuery.value === "") {
          searchQuery.value = getDisplayValueForPlaceholder(newVal);
        } else if (!newVal && searchQuery.value !== "") {
          searchQuery.value = "";
        }
      }
    }, { immediate: true });

    watch(searchQuery, (newQuery) => {
      highlightedIndex.value = 0; // --- Resetear índice al buscar
      if (!props.searchFunction) return;
      if (internalSelected.value && newQuery === getDisplayValueForPlaceholder(internalSelected.value)) return;
      if (debounceTimer) clearTimeout(debounceTimer);
      if (!newQuery || !newQuery.trim()) {
        asyncOptions.value = props.options;
        isLoading.value = false;
        return;
      }
      isLoading.value = true;
      debounceTimer = setTimeout(async () => {
        try {
          const results = await props.searchFunction(newQuery);
          asyncOptions.value = Array.isArray(results) ? results : [];
          highlightedIndex.value = 0; // --- Seleccionar el primero al llegar resultados
        } catch (error) {
          console.error("[SearchableSelect] Error:", error);
          asyncOptions.value = [];
        } finally {
          isLoading.value = false;
        }
      }, props.debounceDelay ?? 100);
    });

    const filteredOptions = computed(() => {
      if (props.searchFunction) return asyncOptions.value;
      if (!searchQuery.value) return props.options;
      const rawQuery = searchQuery.value.toString().toLowerCase().trim();
      const searchTerms = rawQuery.split(/\s+/).filter(term => term.length > 0);
      if (searchTerms.length === 0) return props.options;

      return props.options.filter((option) => {
        if (typeof option !== "object" || option === null) {
          if (option == null) return false;
          const optionStr = option.toString().toLowerCase();
          return searchTerms.every(term => optionStr.includes(term));
        }
        const keysToFilter = props.filterKeys.length > 0 ? props.filterKeys : Object.keys(option);
        const searchableText = keysToFilter.map((key) => {
          if (!Object.prototype.hasOwnProperty.call(option, key)) return "";
          const value = option[key];
          return value != null ? value.toString().toLowerCase() : "";
        }).join(" ");
        return searchTerms.every(term => searchableText.includes(term));
      });
    });

    // --- Manejo de teclado ---
    const handleKeydown = (event) => {
      if (props.isDisabledComponent) return;

      // Si no está abierto, Enter o Abajo lo abren
      if (!showDropdown.value) {
        if (event.key === 'ArrowDown' || event.key === 'Enter') {
          handleFocus();
          event.preventDefault();
        }
        return;
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault(); // Evitar scroll de la página
          if (highlightedIndex.value < filteredOptions.value.length - 1) {
            highlightedIndex.value++;
            scrollToOption();
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (highlightedIndex.value > 0) {
            highlightedIndex.value--;
            scrollToOption();
          }
          break;
        case 'Enter':
          event.preventDefault(); // Evitar submit del form
          if (highlightedIndex.value >= 0 && filteredOptions.value[highlightedIndex.value]) {
            selectOption(filteredOptions.value[highlightedIndex.value]);
          }
          break;
        case 'Escape':
          showDropdown.value = false;
          searchInput.value?.blur();
          break;
        case 'Tab':
          // Dejar que el evento blur cierre el dropdown (comportamiento nativo)
          // Si quieres que Tab seleccione, descomenta abajo:
          // if (highlightedIndex.value >= 0) selectOption(filteredOptions.value[highlightedIndex.value]);
          break;
      }
    };

    const scrollToOption = () => {
      nextTick(() => {
        if (!dropdownListRef.value) return;
        const items = dropdownListRef.value.querySelectorAll('.pe-dropdown-item');
        const activeItem = items[highlightedIndex.value];

        if (activeItem) {
          activeItem.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
      });
    };

    const isDisabled = (option) => {
      if (!props.disabledItems || props.disabledItems.length === 0) return false;
      return props.disabledItems.some((item) => {
        if (typeof option === "object" && typeof item === "object" && option !== null && item !== null) {
          if ("id" in option && "id" in item) return option.id === item.id;
          return JSON.stringify(item) === JSON.stringify(option);
        }
        return item === option;
      });
    };

    const isSelected = (option) => {
      if (!props.selected || (Array.isArray(props.selected) && props.selected.length === 0)) return false;
      if (Array.isArray(props.selected)) {
        return props.selected.some((selectedItem) => {
          if (typeof option === "object" && typeof selectedItem === "object" && option !== null && selectedItem !== null) {
            if ("id" in option && "id" in selectedItem) return option.id === selectedItem.id;
            return JSON.stringify(option) === JSON.stringify(selectedItem);
          }
          return option === selectedItem;
        });
      } else {
        if (typeof option === "object" && typeof props.selected === "object" && option !== null && props.selected !== null) {
          if ("id" in option && "id" in props.selected) return option.id === props.selected.id;
          return JSON.stringify(option) === JSON.stringify(props.selected);
        }
        return option === props.selected;
      }
    };

    const selectOption = (option) => {
      if (props.isDisabledComponent || isDisabled(option)) return;
      internalSelected.value = option;
      emit("select", option);
      if (!props.useLastAsPlaceholder) {
        searchQuery.value = "";
        if (props.searchFunction) asyncOptions.value = props.options;
      } else {
        searchQuery.value = getDisplayValueForPlaceholder(option);
      }
      showDropdown.value = false;
      inputFocused.value = false;
      highlightedIndex.value = -1;
      if (searchInput.value) searchInput.value.blur();
    };

    const handleFocus = async () => {
      if (props.isDisabledComponent) { showDropdown.value = false; return; }
      inputFocused.value = true;
      if (!showDropdown.value) {
        showDropdown.value = true;
        await nextTick();
        emit("dropdown-opened");
      }
      if (internalSelected.value) searchQuery.value = "";

      highlightedIndex.value = filteredOptions.value.length > 0 ? 0 : -1;
    };

    const handleFocusAndSearch = async () => {
      suppressNextHide.value = true;
      inputFocused.value = true;
      await handleFocus();
      searchQuery.value = "";
      await nextTick();
      searchInput.value?.focus();
      setTimeout(() => suppressNextHide.value = false, 300);
    };

    const hideDropdownWithDelay = () => {
      if (suppressNextHide.value) return;
      setTimeout(() => {
        showDropdown.value = false;
        inputFocused.value = false;
        highlightedIndex.value = -1; // --- NUEVO: Reset al cerrar
        if (props.useLastAsPlaceholder && internalSelected.value && searchQuery.value === "") {
          searchQuery.value = getDisplayValueForPlaceholder(internalSelected.value);
        } else if (!props.useLastAsPlaceholder && searchQuery.value !== "") {
          searchQuery.value = "";
        }
      }, 200);
    };

    const displayValue = (option) => {
      if (typeof option !== "object" || option === null) return option;
      const keysToUse = props.displayKeys || props.placeholderKey;
      if (Array.isArray(keysToUse)) {
        const parts = keysToUse.map(key => Object.prototype.hasOwnProperty.call(option, key) ? String(option[key]) : "").filter(Boolean);
        return parts.length > 0 ? parts.join(" ") : "Sin título";
      } else {
        return Object.prototype.hasOwnProperty.call(option, keysToUse) ? String(option[keysToUse]) : "Sin título";
      }
    };

    const getDisplayValueForPlaceholder = (option) => {
      if (typeof option !== "object" || option === null) return option;
      const keysToUse = props.placeholderKey;
      if (Array.isArray(keysToUse)) {
        const parts = keysToUse.map(key => Object.prototype.hasOwnProperty.call(option, key) ? String(option[key]) : "").filter(Boolean);
        return parts.length > 0 ? parts.join(" ") : "";
      } else {
        return option[keysToUse] != null ? String(option[keysToUse]) : "";
      }
    };

    const computedPlaceholder = computed(() => {
      if (props?.useLastAsPlaceholder && internalSelected.value) return getDisplayValueForPlaceholder(internalSelected.value);
      return props.defaultPlaceholder;
    });

    const hasSelection = computed(() => !!internalSelected.value && searchQuery.value === getDisplayValueForPlaceholder(internalSelected.value));
    const hasOptionsToShow = computed(() => props.options?.length >= 0 || !!props.searchFunction);
    const shouldShowDropdown = computed(() => !props.isDisabledComponent && (isLoading.value || filteredOptions.value.length >= 0));

    return {
      isLoading, hasOptionsToShow, shouldShowDropdown, showDropdown, searchInput, searchQuery,
      filteredOptions, computedPlaceholder, hasSelection, isDisabled, isSelected, selectOption,
      handleFocus, handleFocusAndSearch, hideDropdownWithDelay, displayValue,
      isDisabledComp: computed(() => props.isDisabledComponent), inputFocused,
      globalConfig,
      highlightedIndex, handleKeydown, dropdownListRef
    };
  },
};
</script>

<style scoped>
/* WRAPPER */
.pe-select-wrapper {
  position: relative;
  width: 100%;
}

.pe-input-group {
  position: relative;
  width: 100%;
}

/* INPUTS (Heredan y extienden .pe-input de FormOrchestrator) */
.pe-input--fake,
.pe-search-input {
  /* Al usar la clase .pe-input en el template, ya hereda:
     border, background, padding, radius, font, color.
     Aquí solo agregamos comportamientos específicos del select. */
  cursor: text;
  display: flex;
  align-items: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Modificador para cuando hay selección (Texto en negrita por defecto) */
.pe-input--has-selection::placeholder {
  color: inherit;
  /* font-weight: var(--pe-input-font-weight-bold, 600); */
}

/* Modificador sin seleccion */
.pe-input::placeholder {
  /* font-weight: var(--pe-input-font-weight-normal, 400); */
}

/* Input oculto (cuando se muestra el slot de selección) */
.pe-input--hidden {
  position: absolute;
  opacity: 0;
  height: 0;
  width: 0;
  padding: 0;
  margin: 0;
  border: none;
  pointer-events: none;
}

/* Estado deshabilitado del wrapper */
.pe-select-wrapper.is-disabled {
  opacity: 0.7;
  pointer-events: none;
}

/* DROPDOWN LIST CONTAINER */
.pe-dropdown-list {
  /* Variables propias del dropdown con defaults */
  background: var(--pe-dropdown-bg-color, #ffffff);
  border: var(--pe-dropdown-border, 1px solid #ced4da);
  border-radius: var(--pe-dropdown-border-radius, 0.5rem);
  box-shadow: var(--pe-dropdown-shadow, 0 4px 12px rgba(0, 0, 0, 0.1));

  width: 100%;
  max-height: var(--pe-dropdown-max-height, 200px);

  overflow-y: auto;
  list-style: none;
  padding: var(--pe-dropdown-l-padding, 0);
  margin-top: var(--pe-dropdown-list-margin-top, 5px);

  /* Scrollbar fina (Firefox) */
  scrollbar-width: var(--pe-dropdown-l-scrollbar-witdh, thin);
  scrollbar-color: var(--pe-scrollbar-thumb, #ccc) transparent;
}

/* Webkit scrollbar */
.pe-dropdown-list::-webkit-scrollbar {
  width: var(--pe-scrollbar-width, 6px);
}

.pe-dropdown-list::-webkit-scrollbar-thumb {
  background: var(--pe-scrollbar-thumb, #ccc);
  border-radius: var(--pe-scrollbar-thumb-br, 4px);
}

/* Variantes Overlay */
.pe-dropdown-list--overlay {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
}

.pe-dropdown-list--upwards {
  top: auto;
  bottom: 100%;
  margin-top: 0;
  margin-bottom: var(--pe-dropdown-margin-bottom, 5px);
}

/* ITEMS DE LA LISTA */
.pe-dropdown-item {
  padding: var(--pe-option-padding, 0.5rem 1rem);
  color: var(--pe-option-text-color, #333);
  font-size: var(--pe-option-font-size, 0.9rem);
  background: var(--pe-option-bg-color, transparent);
  border: var(--pe-option-border, none);
  border-radius: var(--pe-option-border-radius, 0px);
  cursor: pointer;
  transition: background-color 0.2s;

  display: flex;
  align-items: var(--pe-dropdown-item-align-items, center);
  min-height: var(--pe-dropdown-item-min-height, 35px);
}

.pe-dropdown-item.pe-dropdown-item--highlighted,
.pe-dropdown-item:hover {
  color: var(--pe-dropdown-item-hover-color, inherit);
  background: var(--pe-option-hover-bg, #f0f0f0);
  border: var(--pe-option-border--highlighted, none);
  border-radius: var(--pe-option-border-radius--highlighted, 0px);
}

.pe-dropdown-item--selected {
  color: var(--pe-dropdown-item-selected-color, inherit);
  background: var(--pe-dropdown-item-selected-bg, #e9ecef);
  border: var(--pe-option-border-selected, none);
  border-radius: var(--pe-option-border-radius-selected, 0px);
  cursor: default;
}

.pe-dropdown-item--disabled {
  color: var(--pe-dropdown-item-disabled-color, #aaa);
  background: var(--pe-dropdown-item-disabled-bg, transparent);
  border: var(--pe-option-border-disabled, none);
  border-radius: var(--pe-option-border-radius-disabled, 0px);
  cursor: not-allowed;
}

/* Items informativos (Cargando, No resultados) */
.pe-dropdown-item--info {
  justify-content: var(--pe-dropdown-item-info-justify-content, center);
  color: var(--pe-dropdown-item--color, #6c757d);
  background: var(--pe-dropdown-item-info-bg, transparent);
  cursor: default;
}

/* .pe-dropdown-item.pe-dropdown-item--highlighted,
.pe-dropdown-item--info:hover {
  background: var(--pe-dropdown-item-info-hover-bg, transparent);
} */
</style>